// Appointments and their occurrences (spec 4.6, L06). Local clock time plus time zone; the RRULE engine
// (rrule-temporal) keeps 17:00 at 17:00 across summer and winter time.

import { RRuleTemporal } from "rrule-temporal";
import { addDays, joinLocal, localToInstant, TIME_ZONE } from "./time";

export type RepeatKind = "none" | "weekly" | "monthly";

export interface Repeat {
  kind: RepeatKind;
  interval: number;
  /** ISO weekdays 1-7, for weekly. */
  weekdays: number[];
  /** Last date (inclusive), or null for no end. */
  until: string | null;
}

const BYDAY = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

/**
 * A weekly series always includes the weekday of its first occurrence: the RRULE engine leaves out a start
 * that does not match BYDAY, and then the chosen occurrence would disappear (review wave 3).
 */
export function withStartDay(repeat: Repeat, startLocal: string): Repeat {
  if (repeat.kind !== "weekly") return repeat;
  const d = new Date(`${startLocal.slice(0, 10)}T00:00:00Z`).getUTCDay();
  const weekday = d === 0 ? 7 : d;
  return repeat.weekdays.includes(weekday) ? repeat : { ...repeat, weekdays: [...repeat.weekdays, weekday].sort() };
}

/** RRULE for the repeat settings, or null for a single appointment. */
export function buildRrule(repeat: Repeat): string | null {
  if (repeat.kind === "none") return null;
  const parts = [`FREQ=${repeat.kind === "weekly" ? "WEEKLY" : "MONTHLY"}`];
  if (repeat.interval > 1) parts.push(`INTERVAL=${repeat.interval}`);
  if (repeat.kind === "weekly")
    parts.push(
      `BYDAY=${[...repeat.weekdays]
        .sort()
        .map((d) => BYDAY[d - 1])
        .join(",")}`,
    );
  if (repeat.until) {
    // UNTIL is UTC when DTSTART has a TZID (RFC 5545): the end of the last day in Amsterdam.
    const end = localToInstant(joinLocal(addDays(repeat.until, 1), "00:00")).getTime() - 1000;
    parts.push(
      `UNTIL=${new Date(end)
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/\.\d{3}/, "")}`,
    );
  }
  return parts.join(";");
}

export interface AppointmentLike {
  id: string;
  title: string;
  location: string | null;
  start_local: string;
  duration_minutes: number;
  rrule: string | null;
  remind_minutes: number;
  /** Travel time before it starts (golf 9); the notification then says when to leave. */
  travel_minutes?: number | null;
  deleted_at?: string | Date | null;
}

export interface ExceptionLike {
  appointment_id: string;
  recurrence_local: string;
  cancelled: boolean;
  new_start_local: string | null;
  new_duration_minutes: number | null;
  title: string | null;
  location: string | null;
  deleted_at?: string | Date | null;
}

export interface Occurrence {
  appointmentId: string;
  /** Original start of this occurrence (identifies it in a series). */
  recurrenceLocal: string;
  /** Local start and end, "YYYY-MM-DDTHH:MM:SS". */
  startLocal: string;
  endLocal: string;
  date: string;
  title: string;
  location: string | null;
  remindMinutes: number;
  travelMinutes: number;
  repeats: boolean;
  changed: boolean;
}

/** Normalise "2026-10-14 17:00:00", "2026-10-14T17:00" and Date to "2026-10-14T17:00:00". */
export function normLocal(value: string | Date): string {
  if (value instanceof Date) return value.toISOString().slice(0, 19);
  const s = value.replace(" ", "T");
  return s.length === 16 ? `${s}:00` : s.slice(0, 19);
}

function addMinutesLocal(local: string, minutes: number): string {
  // Wall clock arithmetic: treat the local time as if it were UTC.
  const t = Date.parse(`${local}Z`) + minutes * 60000;
  return new Date(t).toISOString().slice(0, 19);
}

function compact(local: string) {
  return local.replace(/[-:]/g, "");
}

/** Original starts of an appointment within [from, to] (dates, inclusive), as local strings. */
function rawStarts(a: AppointmentLike, from: string, to: string): string[] {
  const start = normLocal(a.start_local);
  if (!a.rrule) return start.slice(0, 10) >= from && start.slice(0, 10) <= to ? [start] : [];
  const rule = new RRuleTemporal({ rruleString: `DTSTART;TZID=${TIME_ZONE}:${compact(start)}\nRRULE:${a.rrule}` });
  const after = localToInstant(joinLocal(from, "00:00"));
  const before = localToInstant(joinLocal(addDays(to, 1), "00:00"));
  return rule
    .between(after, before, true)
    .map((z) => z.toPlainDateTime().toString().slice(0, 19))
    .filter((s) => s.slice(0, 10) >= from && s.slice(0, 10) <= to);
}

/** All occurrences that start between two dates (inclusive), with changed and skipped ones applied. */
export function occurrencesBetween(appointments: AppointmentLike[], exceptions: ExceptionLike[], from: string, to: string): Occurrence[] {
  const out: Occurrence[] = [];
  const live = exceptions.filter((e) => !e.deleted_at);
  for (const a of appointments) {
    if (a.deleted_at) continue;
    const own = new Map(live.filter((e) => e.appointment_id === a.id).map((e) => [normLocal(e.recurrence_local), e]));
    const raw = rawStarts(a, addDays(from, -7), addDays(to, 7));
    // An occurrence moved from further away still shows where it was moved to.
    const seen = new Set(raw);
    for (const [recurrence, ex] of own) {
      if (!seen.has(recurrence) && !ex.cancelled && ex.new_start_local) {
        const date = normLocal(ex.new_start_local).slice(0, 10);
        if (date >= from && date <= to) raw.push(recurrence);
      }
    }
    for (const recurrence of raw) {
      const ex = own.get(recurrence);
      if (ex?.cancelled) continue;
      const startLocal = ex?.new_start_local ? normLocal(ex.new_start_local) : recurrence;
      const date = startLocal.slice(0, 10);
      if (date < from || date > to) continue;
      const duration = ex?.new_duration_minutes ?? a.duration_minutes;
      out.push({
        appointmentId: a.id,
        recurrenceLocal: recurrence,
        startLocal,
        endLocal: addMinutesLocal(startLocal, duration),
        date,
        title: ex?.title ?? a.title,
        location: ex?.location ?? a.location,
        remindMinutes: a.remind_minutes,
        travelMinutes: a.travel_minutes ?? 0,
        repeats: !!a.rrule,
        changed: !!ex,
      });
    }
  }
  return out.sort((x, y) => (x.startLocal < y.startLocal ? -1 : x.startLocal > y.startLocal ? 1 : 0));
}

/** The repeat settings of an appointment row, for the form. */
export function repeatOf(a: {
  repeat_kind: RepeatKind;
  repeat_interval: number;
  repeat_weekdays: number[];
  repeat_until: string | null;
}): Repeat {
  return { kind: a.repeat_kind, interval: a.repeat_interval, weekdays: a.repeat_weekdays, until: a.repeat_until };
}

/** The day before an occurrence: where a series ends when "deze en volgende" changes it. */
export function dayBefore(localStart: string): string {
  return addDays(localStart.slice(0, 10), -1);
}
